import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Users, Phone, MapPin, Megaphone, Home } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
const volunteerOpportunities = [
  {
    icon: Phone,
    title: "Phone Banking",
    description: "Help us reach voters by making calls from home or our campaign office."
  },
  {
    icon: MapPin,
    title: "Door Knocking",
    description: "Meet your neighbors and share Matt's vision for the 33rd District."
  },
  {
    icon: Megaphone,
    title: "Event Support",
    description: "Help organize and staff campaign events and community gatherings."
  },
  {
    icon: Home,
    title: "Host a Meet & Greet",
    description: "Open your home to neighbors and introduce them to Matt."
  }
];

const Volunteer = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    interests: [] as string[],
    message: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-volunteer-email", {
        body: formData,
      });
      if (error) throw error;
      toast({
        title: "Thank you for signing up!",
        description: "A member of our campaign team will be in touch soon.",
      });
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        interests: [],
        message: ""
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Something went wrong",
        description: "Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleInterest = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  return (
    <>
      <Helmet>
        <title>Volunteer | Matt Bucher for State Representative</title>
        <meta name="description" content="Join the Matt Bucher campaign! Volunteer to help elect a conservative leader to the 33rd District." />
        <link rel="canonical" href="https://votebucher.com/volunteer" />
        <meta property="og:url" content="https://votebucher.com/volunteer" />
      </Helmet>
      
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main id="main-content" className="flex-grow">
          {/* Hero Banner */}
          <section className="bg-primary py-20" aria-labelledby="volunteer-heading">
            <div className="container mx-auto px-4 text-center">
              <Users className="h-16 w-16 mx-auto mb-6 text-primary-foreground" aria-hidden="true" />
              <h1 id="volunteer-heading" className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-4">
                Join Our Team
              </h1>
              <p className="font-body text-xl text-primary-foreground/90 max-w-2xl mx-auto">
                Together, we can bring conservative leadership to the 33rd District. Your time and talents can make all the difference.
              </p>
            </div>
          </section>

          {/* Volunteer Opportunities */}
          <section className="py-16 bg-background" aria-labelledby="ways-heading">
            <div className="container mx-auto px-4">
              <h2 id="ways-heading" className="font-display text-3xl font-bold text-foreground mb-8 text-center">
                Ways to Help
              </h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" role="list">
                {volunteerOpportunities.map((opp) => (
                  <li key={opp.title} className="bg-card p-6 rounded-lg border border-border shadow-sm text-center">
                    <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                      <opp.icon className="h-7 w-7 text-primary" aria-hidden="true" />
                    </div>
                    <h3 className="font-display text-lg font-bold text-foreground mb-2">
                      {opp.title}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground">
                      {opp.description}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Volunteer Form */}
          <section className="py-16 bg-card" aria-labelledby="signup-heading">
            <div className="container mx-auto px-4">
              <div className="max-w-2xl mx-auto">
                <h2 id="signup-heading" className="font-display text-3xl font-bold text-foreground mb-4 text-center">
                  Sign Up to Volunteer
                </h2>
                <p className="font-body text-muted-foreground mb-8 text-center">
                  Fill out the form below and a campaign volunteer coordinator will contact you.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6" aria-labelledby="signup-heading">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName" className="font-body font-semibold">First Name *</Label>
                      <Input
                        id="firstName"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="font-body font-semibold">Last Name *</Label>
                      <Input
                        id="lastName"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="font-body font-semibold">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="font-body font-semibold">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address" className="font-body font-semibold">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      className="mt-1"
                      placeholder="City, State"
                    />
                  </div>

                  <div>
                    <Label className="font-body font-semibold mb-3 block">I'm interested in: (select all that apply)</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {volunteerOpportunities.map((opp) => (
                        <div key={opp.title} className="flex items-center gap-2">
                          <Checkbox
                            id={opp.title}
                            checked={formData.interests.includes(opp.title)}
                            onCheckedChange={() => toggleInterest(opp.title)}
                          />
                          <Label htmlFor={opp.title} className="font-body cursor-pointer">
                            {opp.title}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-body font-semibold">Additional Comments</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="mt-1"
                      rows={4}
                      placeholder="Tell us about yourself or any questions you have..."
                    />
                  </div>

                  <Button type="submit" variant="cta" size="xl" className="w-full" disabled={submitting}>
                    {submitting ? "Sending..." : "Sign Up Now"}
                  </Button>
                </form>
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default Volunteer;
